<script setup lang="ts">
import { ErrorMessage, Field, Form, useForm } from "vee-validate";
import { ref } from "vue";
import { object, string, ref as yupRef } from "yup";
import countries from "@/data/countries.json";
import countryCodes from "@/data/country-codes.json";
import {
  type EnquiryCreateResponse,
  isDuplicateEnquiryResponse,
} from "@/utils/enquiryCreate";

const props = defineProps<{
  trekRoute: string;
  trekTitle: string;
  trekPrice: string;
  trekPersonDay: string;
  trekDates?: string;
  placesAvailable?: string;
  availability?: string;
}>();

const emit = defineEmits<{ close: []; success: [] }>();

const route = useRoute();
const dateTime = ref(new Date().toLocaleString());
const firstName = ref("");
const lastName = ref("");
const title = ref("");
const email = ref("");
const confirmEmail = ref("");
const countryCode = ref("");
const phone = ref("");
const country = ref("");
const arrivalDate = ref("");
const numPeople = ref("");
const enquiry = ref("");
const hearAbout = ref("");
const hearOther = ref("");
const marketingConsent = ref<string>("");
const honeypot = ref("");

// Pre-built trek package string for display
const trekPackageLabel = computed(
  () => `${props.trekRoute} – ${props.trekTitle}`,
);

const emailRegex = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const phoneRegex = /^[1-9]\d{1,14}$/;
// Optional preferred arrival date: enforce MM/YYYY when provided
const monthYearRegex = /^(0?[1-9]|1[0-2])\/\d{4}$/;

const schema = object().shape({
  title: string().required("Please enter title"),
  firstName: string().required("Please enter first name"),
  lastName: string().required("Please enter last name"),
  email: string()
    .matches(emailRegex, "Please enter a valid email address")
    .required("Please enter an email"),
  confirmEmail: string()
    .oneOf([yupRef("email")], "Emails must match")
    .required("Please confirm your email"),
  countryCode: string().required("Please select country code"),
  phone: string()
    .required("Please enter a phone number")
    .matches(phoneRegex, "Phone number must contain digits only"),
  country: string().required("Please select country"),
  arrivalDate: string()
    .nullable()
    .test(
      "arrival-date-format",
      "Please enter date as MM/YYYY",
      (value) => !value || monthYearRegex.test(value),
    ),
  numPeople: string().required("Please select group size"),
  enquiry: string().required("Please enter your enquiry"),
});

const { handleSubmit } = useForm();

const marketingConsentChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  marketingConsent.value = target?.checked ? "Yes please" : "No thanks";
};

const sending = ref(false);
const emailSent = ref(false);
const isDuplicateSubmission = ref(false);
const submitError = ref("");

const onSubmit = handleSubmit(async () => {
  if (honeypot.value !== "") return;

  sending.value = true;
  submitError.value = "";

  const payload = {
    first_name: firstName.value.trim(),
    last_name: lastName.value.trim(),
    email: email.value.trim(),
    phone: phone.value.trim() || undefined,
    title: title.value || undefined,
    country: country.value || undefined,
    country_code: countryCode.value || undefined,
    message: enquiry.value.trim() || undefined,
    marketing_consent: marketingConsent.value || undefined,
    source_page: typeof route.path === "string" ? route.path : "/",
    submission_id: crypto.randomUUID(),
    enquiry_type: "landing page enquiry",
    date_time: new Date().toLocaleString(),
    arrival_date: arrivalDate.value || undefined,
    num_people: numPeople.value || undefined,
    hear_about: hearAbout.value || undefined,
    hear_other: hearOther.value || undefined,
    trek_route: props.trekRoute,
    trek_title: props.trekTitle,
    trek_package: trekPackageLabel.value,
    trek_price: props.trekPrice,
    trek_person_day: props.trekPersonDay,
    trek_dates: props.trekDates ?? undefined,
    places_available: props.placesAvailable ?? undefined,
    availability: props.availability ?? undefined,
    user_agent: navigator.userAgent,
  };

  try {
    const response = await $fetch<EnquiryCreateResponse>("/api/enquiries/create", {
      method: "POST",
      body: payload,
    });
    isDuplicateSubmission.value = isDuplicateEnquiryResponse(response);
    emailSent.value = true;
    if (!isDuplicateSubmission.value) {
      emit("success");
    }
  } catch (err: unknown) {
    const message =
      err && typeof err === "object" && "data" in err && err.data && typeof (err.data as { message?: string }).message === "string"
        ? (err.data as { message: string }).message
        : "Something went wrong. Please try again.";
    submitError.value = message;
  } finally {
    sending.value = false;
  }
});
</script>

<template>
  <ClientOnly>
    <div class="contact-form trek-enquiry-form">
      <!-- Trek package summary shown at top of form -->
      <div class="trek-enquiry-summary">
        <div class="trek-enquiry-summary-info">
          <h3 class="trek-enquiry-route">
            {{ trekRoute }}

            <span v-if="trekRoute.includes('The Italian Dolomites')"
              >– Alta Via 1</span
            >
          </h3>
          <h4 class="trek-enquiry-title">
            {{ trekTitle }} <span v-if="trekDates">– {{ trekDates }}</span>
          </h4>
        </div>
      </div>

      <Form
        v-if="!emailSent"
        id="trekEnquiryForm"
        :validation-schema="schema"
        @submit="onSubmit"
      >
        <!-- Hidden trek package fields -->
        <input v-model="dateTime" type="hidden" name="dateTime" />
        <input :value="trekRoute" type="hidden" name="trekRoute" />
        <input :value="trekTitle" type="hidden" name="trekTitle" />
        <input :value="trekDates" type="hidden" name="trekDates" />
        <input :value="trekPackageLabel" type="hidden" name="trekPackage" />

        <input
          :value="placesAvailable ?? ''"
          type="hidden"
          name="placesAvailable"
        />
        <input :value="availability ?? ''" type="hidden" name="availability" />

        <!-- Honeypot -->
        <input
          v-model="honeypot"
          type="text"
          name="website"
          class="honeypot-field"
          autocomplete="off"
          tabindex="-1"
          aria-hidden="true"
        />

        <div class="form-col col-3">
          <div class="form-control">
            <label for="trek-title">Title</label>
            <div class="select-wrapper">
              <Field
                id="trek-title"
                v-model="title"
                as="select"
                name="title"
                class="select-styled"
              >
                <option value="" disabled>Please select...</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
                <option value="Miss">Miss</option>
                <option value="Mx">Mx</option>
              </Field>
            </div>
            <Transition name="error" appear>
              <ErrorMessage class="error" name="title" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="trek-firstName">First Name</label>
            <Field
              id="trek-firstName"
              v-model="firstName"
              type="text"
              name="firstName"
              placeholder="First name*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="firstName" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="trek-lastName">Last Name</label>
            <Field
              id="trek-lastName"
              v-model="lastName"
              type="text"
              name="lastName"
              placeholder="Last name*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="lastName" />
            </Transition>
          </div>
        </div>

        <div class="form-col col-2">
          <div class="form-control">
            <label for="trek-email">Email</label>
            <Field
              id="trek-email"
              v-model="email"
              type="email"
              name="email"
              placeholder="Email*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="email" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="trek-confirmEmail">Confirm Email</label>
            <Field
              id="trek-confirmEmail"
              v-model.trim="confirmEmail"
              name="confirmEmail"
              type="email"
              placeholder="Re-type Email*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="confirmEmail" />
            </Transition>
          </div>
        </div>

        <div class="form-col col-3">
          <div class="form-control">
            <label for="trek-countryCode">Int. Dialling Code</label>
            <div class="select-wrapper">
              <Field
                id="trek-countryCode"
                v-model="countryCode"
                as="select"
                name="countryCode"
                class="select-styled"
              >
                <option value="" disabled>Please select...</option>
                <option
                  v-for="code in countryCodes"
                  :key="code.value"
                  :value="code.value"
                >
                  {{ code.name }}
                </option>
              </Field>
            </div>
            <Transition name="error" appear>
              <ErrorMessage class="error" name="countryCode" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="trek-phone">Telephone</label>
            <Field
              id="trek-phone"
              v-model="phone"
              type="tel"
              name="phone"
              placeholder="Phone number*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="phone" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="trek-country">Country of Residence</label>
            <div class="select-wrapper country">
              <Field
                id="trek-country"
                v-model.trim="country"
                as="select"
                name="country"
                class="select-styled"
              >
                <option value="" disabled>Please select...</option>
                <option
                  v-for="code in countries"
                  :key="code.value"
                  :value="code.value"
                >
                  {{ code.label }}
                </option>
              </Field>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="country" />
              </Transition>
            </div>
          </div>
        </div>

        <div class="form-col col-2">
          <div class="form-control">
            <label for="trek-numPeople">Group Size</label>
            <Field
              id="trek-numPeople"
              v-model="numPeople"
              type="number"
              name="numPeople"
              placeholder="Number of people*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="numPeople" />
            </Transition>
          </div>
        </div>

        <div class="form-col col-1">
          <div class="form-control">
            <label for="trek-enquiry">Message</label>
            <Field
              id="trek-enquiry"
              v-model="enquiry"
              as="textarea"
              type="textarea"
              class="textarea"
              name="enquiry"
              placeholder="Message..."
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="enquiry" />
            </Transition>
          </div>
        </div>

        <div class="form-col col-2">
          <div class="form-control">
            <label for="trek-hearAbout">How did you hear about us?</label>
            <div class="select-wrapper">
              <Field
                id="trek-hearAbout"
                v-model="hearAbout"
                as="select"
                name="hearAbout"
                class="select-styled"
              >
                <option value="" selected disabled>Please select...</option>
                <option value="Google Search">Google search</option>
                <option value="Word of Mouth/Recommendation">
                  Word of mouth/recommendation
                </option>
                <option value="Existing client">Existing client</option>
                <option value="Instagram">Instagram</option>
                <option value="Facebook">Facebook</option>
                <option value="Blog">Blog</option>
                <option value="Other">Other</option>
              </Field>
            </div>
          </div>
          <div class="form-control">
            <label for="trek-hearOther">If other please specify</label>
            <Field
              id="trek-hearOther"
              v-model="hearOther"
              type="text"
              name="hearOther"
              placeholder="If other please specify"
            />
          </div>
        </div>

        <div class="checkbox-wrap">
          <div class="form-col col-2">
            <div class="form-control">
              <AppFormCustomCheckbox
                v-model="marketingConsent"
                name="agreeTerms"
                value="I agree with Mont Blanc Trek's privacy statement which can be found in the footer of this page"
              />
            </div>
            <div class="form-control">
              <AppFormCustomCheckbox
                v-model="marketingConsent"
                name="marketingConsent"
                value="Please contact me over email with related information"
                @change="marketingConsentChange($event)"
              />
            </div>
          </div>
        </div>

        <Field
          v-model="marketingConsent"
          type="hidden"
          name="marketingConsent"
        />

        <div v-if="submitError" class="form-error">
          {{ submitError }}
        </div>
        <div class="form-actions">
          <button class="submit-btn btn" type="submit" :disabled="sending">
            <span v-if="sending">Sending...</span>
            <span v-else>Send Enquiry</span>
          </button>
        </div>
      </Form>

      <Transition name="slideleft" appear>
        <div
          v-if="emailSent"
          id="trekFormEndScreen"
          class="form-success"
          :class="{ 'form-end-screen--duplicate': isDuplicateSubmission }"
        >
          <template v-if="isDuplicateSubmission">
            <h3>We already have your enquiry</h3>
            <p class="form-end-screen-note">
              We received your details for
              <strong>{{ trekPackageLabel }}</strong>
              a few moments ago. There is no need to send the form again — we
              will be in touch shortly.
            </p>
          </template>
          <template v-else>
            <h3>
              Thanks for your
              <span v-if="country === 'United States'">inquiry!</span>
              <span v-else>enquiry!</span>
            </h3>
            <p>
              We've received your enquiry for
              <strong>{{ trekPackageLabel }}</strong>
              and will be in touch shortly.
            </p>
          </template>
          <button class="btn" @click="emit('close')">Close</button>
        </div>
      </Transition>
    </div>
  </ClientOnly>
</template>

<style scoped>
.honeypot-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
